export const theme = {
  colors: {
    // DARK PROFESSIONAL PALETTE
    primary: {
      50: "#fafafa", // Very light gray
      100: "#f4f4f5", // Light gray
      200: "#e4e4e7", // Medium light gray
      300: "#d4d4d8", // Medium gray
      400: "#a1a1aa", // Gray
      500: "#71717a", // Dark gray
      600: "#52525b", // Darker gray
      700: "#3f3f46", // Very dark gray
      800: "#27272a", // Deep dark gray
      900: "#18181b", // Near black
    },

    // SOPHISTICATED DARK ACCENTS
    accent: {
      primary: "#dc2626", // Professional red
      secondary: "#991b1b", // Deep red
      tertiary: "#7f1d1d", // Dark red
      quaternary: "#450a0a", // Very dark red
      crimson: "#ef4444", // Bright red accent
      charcoal: "#374151", // Dark charcoal
      silver: "#9ca3af", // Silver gray
      light: "#f3f4f6", // Light gray for contrast
      success: "#059669", // Professional green (kept for success states)
      warning: "#f59e0b", // Orange warning
      error: "#dc2626", // Red error
      info: "#6366f1", // Professional purple-blue
    },

    // DARK SOPHISTICATED GRADIENTS
    gradients: {
      primary: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)",
      secondary: "linear-gradient(135deg, #27272a 0%, #18181b 100%)",
      tertiary: "linear-gradient(135deg, #7f1d1d 0%, #450a0a 100%)",
      background: "linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%)",
      subtle: "linear-gradient(135deg, #fafafa 0%, #f4f4f5 100%)",
      hero: "linear-gradient(135deg, #18181b 0%, #27272a 100%)",
      accent: "linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)",
      dark: "linear-gradient(135deg, #374151 0%, #27272a 100%)",
    },

    // UI STATES - DARK THEME
    success: "#059669",
    warning: "#f59e0b",
    error: "#dc2626",
    info: "#6366f1",

    // TEXT COLORS - HIGH CONTRAST
    text: {
      primary: "#111827", // Very dark for light backgrounds
      secondary: "#4b5563", // Medium gray
      muted: "#9ca3af", // Light gray
      accent: "#dc2626", // Brand red
      inverse: "#ffffff", // White text for dark backgrounds
      light: "#f9fafb", // Light text for dark backgrounds
      dark: "#18181b", // Dark text for light backgrounds
    },

    // SURFACE COLORS - LIGHT AND DARK
    surface: {
      primary: "#ffffff", // Pure white
      secondary: "#f9fafb", // Very light gray
      elevated: "#ffffff", // White with shadow
      border: "#e5e7eb", // Light border
      input: "#f3f4f6", // Input background
      hover: "#f3f4f6", // Light gray hover
      dark: "#18181b", // Dark surface
      darkSecondary: "#27272a", // Dark secondary surface
      darkBorder: "#374151", // Dark border
      darkHover: "#3f3f46", // Dark hover state
    },
  },

  typography: {
    fonts: {
      primary:
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      heading:
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      code: '"JetBrains Mono", "Fira Code", "SF Mono", Monaco, Inconsolata, "Roboto Mono", monospace',
    },

    weights: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
      black: 900,
    },

    sizes: {
      xs: "0.75rem", // 12px
      sm: "0.875rem", // 14px
      base: "1rem", // 16px
      lg: "1.125rem", // 18px
      xl: "1.25rem", // 20px
      "2xl": "1.5rem", // 24px
      "3xl": "1.875rem", // 30px
      "4xl": "2.25rem", // 36px
      "5xl": "3rem", // 48px
      "6xl": "3.75rem", // 60px
      "7xl": "4.5rem", // 72px
      "8xl": "6rem", // 96px
      "9xl": "8rem", // 128px
    },

    lineHeights: {
      tight: 1.25,
      snug: 1.375,
      normal: 1.5,
      relaxed: 1.625,
      loose: 2,
    },
  },

  spacing: {
    0: "0",
    1: "0.25rem", // 4px
    2: "0.5rem", // 8px
    3: "0.75rem", // 12px
    4: "1rem", // 16px
    5: "1.25rem", // 20px
    6: "1.5rem", // 24px
    8: "2rem", // 32px
    10: "2.5rem", // 40px
    12: "3rem", // 48px
    16: "4rem", // 64px
    20: "5rem", // 80px
    24: "6rem", // 96px
    32: "8rem", // 128px
    40: "10rem", // 160px
    48: "12rem", // 192px
    56: "14rem", // 224px
    64: "16rem", // 256px
  },

  borderRadius: {
    none: "0",
    sm: "0.125rem", // 2px
    base: "0.25rem", // 4px
    md: "0.375rem", // 6px
    lg: "0.5rem", // 8px
    xl: "0.75rem", // 12px
    "2xl": "1rem", // 16px
    "3xl": "1.5rem", // 24px
    full: "9999px",
  },

  shadows: {
    sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    base: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    inner: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)",

    // Professional elevation shadows
    card: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    elevated:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    floating:
      "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",

    // Enhanced shadows with glow effects
    glow: "0 0 20px rgba(220, 38, 38, 0.3), 0 0 40px rgba(220, 38, 38, 0.2)",
    glowLg: "0 0 30px rgba(220, 38, 38, 0.4), 0 0 60px rgba(220, 38, 38, 0.3)",
    glowXl: "0 0 40px rgba(220, 38, 38, 0.5), 0 0 80px rgba(220, 38, 38, 0.4)",
    crimsonGlow:
      "0 0 25px rgba(239, 68, 68, 0.3), 0 0 50px rgba(239, 68, 68, 0.2)",
    darkGlow: "0 0 30px rgba(39, 39, 42, 0.6), 0 0 60px rgba(39, 39, 42, 0.4)",

    // Interactive shadows
    hoverCard:
      "0 20px 40px rgba(0, 0, 0, 0.15), 0 8px 20px rgba(220, 38, 38, 0.1)",
    hoverButton:
      "0 15px 35px rgba(220, 38, 38, 0.3), 0 5px 15px rgba(0, 0, 0, 0.1)",
    activeButton:
      "0 8px 20px rgba(220, 38, 38, 0.4), 0 3px 8px rgba(0, 0, 0, 0.15)",
  },

  breakpoints: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
  },

  animations: {
    duration: {
      instant: "50ms",
      fast: "150ms",
      normal: "250ms",
      slow: "400ms",
      slower: "600ms",
      slowest: "800ms",
      ultra: "1200ms",
      mega: "2000ms",
    },

    easing: {
      linear: "linear",
      ease: "cubic-bezier(0.25, 0.1, 0.25, 1)",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      easeOut: "cubic-bezier(0, 0, 0.2, 1)",
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      bounce: "cubic-bezier(0.34, 1.56, 0.64, 1)",
      elastic: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
      spring: "cubic-bezier(0.175, 0.885, 0.32, 1.275)",
      smooth: "cubic-bezier(0.33, 1, 0.68, 1)",
    },

    // Enhanced keyframes for complex animations
    keyframes: {
      float:
        "0% { transform: translateY(0px); } 50% { transform: translateY(-20px); } 100% { transform: translateY(0px); }",
      pulse: "0%, 100% { opacity: 1; } 50% { opacity: 0.5; }",
      spin: "from { transform: rotate(0deg); } to { transform: rotate(360deg); }",
      ping: "75%, 100% { transform: scale(2); opacity: 0; }",
      bounce:
        "0%, 100% { transform: translateY(-25%); animation-timing-function: cubic-bezier(0.8, 0, 1, 1); } 50% { transform: translateY(0); animation-timing-function: cubic-bezier(0, 0, 0.2, 1); }",
      shimmer:
        "0% { background-position: -1000px 0; } 100% { background-position: 1000px 0; }",
      glow: "0%, 100% { box-shadow: 0 0 5px rgba(220, 38, 38, 0.5); } 50% { box-shadow: 0 0 20px rgba(220, 38, 38, 0.8), 0 0 30px rgba(220, 38, 38, 0.6); }",
    },
  },

  zIndex: {
    hide: -1,
    auto: "auto",
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
    fixed: 1000,
  },

  // Layout containers
  container: {
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1400px",
  },
};

export type Theme = typeof theme;
